import React, { useState } from "react";
import { Container } from "react-bootstrap";
import { useAppContext } from "../../../context/appContext";
import { isViewOnly } from "../../../utils/permissions";
import TooltipInfo from "../../../components/TooltipInfo";
import { AssignedCategoryPage, UnAssignedCategoryPage } from "./ListSections";

const VersionsCategoriesListPage = ({
  env,
  handleEnvChange,
  currentTenant,
  groups,
  groupVersions,
  currentGroupVersion,
  versionsCategories,
  ogmpCategories,
  technologies,
  isLoading,
  isError
}) => {
  const [expandedParentCategories, setExpandedParentCategories] = useState({});

  const { user } = useAppContext();
  const viewOnly = isViewOnly(user);

  const currentTenantName = localStorage.getItem("selectedTenantName") || null;

  // get unique parent category IDs from ogmpCategories
  const uniqueParentIds = [...new Set(ogmpCategories?.map((cat) => cat.parentCategoryId))];

  const assignedCategoryIds = versionsCategories?.map((vc) => vc.ogmpCategoryId);

  const assignedCategories = ogmpCategories?.filter((cat) => assignedCategoryIds?.includes(cat.id));

  const unassignedCategories = ogmpCategories?.filter((cat) => !assignedCategoryIds?.includes(cat.id));

  // group assigned categories by parent category ID
  const categoriesByParent = uniqueParentIds?.reduce((acc, parentId) => {
    acc[parentId] = assignedCategories?.filter((cat) => cat.parentCategoryId === parentId);
    return acc;
  }, {});

  // group unassigned categories by parent
  const unassignedByParent = uniqueParentIds?.reduce((acc, parentId) => {
    acc[parentId] = unassignedCategories?.filter((cat) => cat.parentCategoryId === parentId);
    return acc;
  }, {});

  const hasAssignedCategories = assignedCategories?.length !== 0;
  const hasOgmpCategories = ogmpCategories?.length !== 0;

  // sort entries and put "No Parent Category" and "No Parent Category" at the end
  const sortEntriesByParentName = (entries) => {
    return [...entries].sort((a, b) => {
      const [parentIdA] = a;
      const [parentIdB] = b;

      // find parent category names
      const parentA = ogmpCategories?.find((cat) => cat.id === parentIdA);
      const parentB = ogmpCategories?.find((cat) => cat.id === parentIdB);

      const nameA = parentA && parentA.name ? parentA.name : "No Parent Category";
      const nameB = parentB && parentB.name ? parentB.name : "No Parent Category";

      // edge case: put "No Parent Category" at the end
      if (nameA === "No Parent Category" && nameB !== "No Parent Category") {
        return 1;
      }
      if (nameA !== "No Parent Category" && nameB === "No Parent Category") {
        return -1;
      }

      // edge case: put "No Parent Category" at the end too
      if (nameA === "No Parent Category" && nameB !== "No Parent Category") {
        return 1;
      }
      if (nameA !== "No Parent Category" && nameB === "No Parent Category") {
        return -1;
      }

      // normal alphabetical sorting for other cases (except edge case for unknown category)
      return nameA.localeCompare(nameB);
    });
  };

  // find the technology name by ID
  const getTechnologyName = (technologyId) => {
    if (!technologyId) return "N/A";
    const tech = technologies.find((t) => t.id === technologyId);
    return tech ? tech.name : "N/A";
  };

  const toggleParentCategory = (parentId) => {
    setExpandedParentCategories((prev) => ({
      ...prev,
      [parentId]: !prev[parentId]
    }));
  };

  // handle localStorage issues with undefined/null for older browsers
  const isTenantSelected =
    currentTenantName !== null &&
    String(currentTenantName) !== "undefined" &&
    String(currentTenantName) !== "null" &&
    currentTenantName !== "";

  const isGroupVersionSelected =
    currentGroupVersion !== null &&
    String(currentGroupVersion) !== "undefined" &&
    String(currentGroupVersion) !== "null" &&
    currentGroupVersion !== "";

  const tenantHasGroups = isTenantSelected && groups?.length >= 1;
  const tenantHasGroupVersions = isTenantSelected && groupVersions?.length >= 1;

  const tenantHasGroupsAndVersionSelected = tenantHasGroups && isGroupVersionSelected;

  const tenantHasGroupsAndVersions = tenantHasGroups && tenantHasGroupVersions;

  if (isLoading || isError || !isTenantSelected) {
    return isLoading ? (
      <Container>
        <p>Loading version categories...</p>
      </Container>
    ) : isError ? (
      <Container>
        <h5>Error loading version categories</h5>
      </Container>
    ) : (
      <Container>
        <h5>Choose a tenant and an OGMP group version to view version categories.</h5>
      </Container>
    );
  }

  // sort the categoriesByParent entries to put "No Parent Category" at the end
  const sortedCategoriesByParent = sortEntriesByParentName(Object.entries(categoriesByParent));

  // sort the unassignedByParent entries to put "No Parent Category" at the end
  const sortedUnassignedByParent = sortEntriesByParentName(Object.entries(unassignedByParent));

  return !tenantHasGroups ? (
    <TooltipInfo
      message="Chosen Tenant does not have a project group."
      tooltipText="You can start by adding a group under the Project Group tab."
    />
  ) : !tenantHasGroupsAndVersionSelected ? (
    <TooltipInfo message="Choose ogmp group version." tooltipText="You can also add new versions under the Group Versions tab." />
  ) : tenantHasGroupsAndVersions ? (
    <Container fluid className="mt-2">
      <AssignedCategoryPage
        sortedCategoriesByParent={sortedCategoriesByParent}
        ogmpCategories={ogmpCategories}
        versionsCategories={versionsCategories}
        expandedParentCategories={expandedParentCategories}
        toggleParentCategory={toggleParentCategory}
        getTechnologyName={getTechnologyName}
        viewOnly={viewOnly}
        groupVersionId={currentGroupVersion}
        hasAssignedCategories={hasAssignedCategories}
        hasOgmpCategories={hasOgmpCategories}
        technologies={technologies}
        env={env}
        handleEnvChange={handleEnvChange}
        currentTenant={currentTenant}
        tenantName={currentTenantName}
      />

      {/* Gap between sections */}
      <div style={{ height: "30px" }}></div>

      <UnAssignedCategoryPage
        sortedUnassignedByParent={sortedUnassignedByParent}
        ogmpCategories={ogmpCategories}
        expandedParentCategories={expandedParentCategories}
        toggleParentCategory={toggleParentCategory}
        viewOnly={viewOnly}
        hasOgmpCategories={hasOgmpCategories}
        technologies={technologies}
        handleEnvChange={handleEnvChange}
        groupVersionId={currentGroupVersion}
        env={env}
        currentTenant={currentTenant}
        tenantName={currentTenantName}
      />
    </Container>
  ) : (
    <Container>
      <h5>Choose a tenant and an OGMP group version to view version categories.</h5>
    </Container>
  );
};

export default VersionsCategoriesListPage;
